/**
 * Composant de gestion de l'authentification à deux facteurs
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Gestion complète des paramètres 2FA avec configuration TOTP et codes de backup.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { use2FA } from '../../hooks';
import { useAuthStore } from '../../store/authStore';
import { TOTPSetupForm } from '../forms/TOTPSetupForm';
import { EmailSetupForm } from '../forms/EmailSetupForm';
import { BackupCodesDisplay } from '../ui/BackupCodesDisplay';
import { SecurityAlert, useSecurityAlert } from '../ui/SecurityAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';

/**
 * Type pour la méthode 2FA
 */
type TwoFactorMethod = 'EMAIL' | 'APPLICATION';

/**
 * Props du composant TwoFactorSettings
 */
export interface TwoFactorSettingsProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Méthode 2FA actuelle (si activée) */
  currentMethod?: TwoFactorMethod;
  /** 2FA est-elle activée ? */
  is2FAEnabled?: boolean;
  /** Nom de l'application (pour le QR code) */
  appName?: string;
  /** Callback après activation réussie */
  onEnabled?: () => void;
  /** Callback après désactivation */
  onDisabled?: () => void;
  /** Callback pour retourner au dashboard ou à la page précédente */
  onBack?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Mode d'affichage
 */
type ViewMode = 'status' | 'method-selection' | 'email-setup' | 'setup' | 'backup';

/**
 * Composant TwoFactorSettings
 *
 * @example
 * ```tsx
 * function TwoFactorPage() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <TwoFactorSettings
 *       userId={user.id}
 *       currentMethod={user.methode2FA}
 *       is2FAEnabled={user.besoin2FA}
 *       appName="Mon Application"
 *     />
 *   );
 * }
 * ```
 */
export function TwoFactorSettings({
  userId,
  currentMethod,
  is2FAEnabled = false,
  appName = 'Application',
  onEnabled,
  onDisabled,
  onBack,
  className = '',
}: TwoFactorSettingsProps) {
  const {
    deactivate2FA,
    generateBackupCodes,
    isLoading,
    error,
    backupCodes,
    clearError,
    clearData,
  } = use2FA();

  const { alert, showAlert, hideAlert } = useSecurityAlert();
  const { getProfile } = useAuthStore();
  const { sendEmailCode } = use2FA();

  const [viewMode, setViewMode] = useState<ViewMode>('status');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [codeSentForDisable, setCodeSentForDisable] = useState(false);

  /**
   * Gérer l'activation réussie de 2FA
   */
  const handleSetupSuccess = async (codes: string[]) => {
    // Rafraîchir le profil utilisateur pour obtenir le nouveau statut 2FA
    await getProfile();

    setViewMode('status');
    showAlert('success', 'Authentification à deux facteurs activée avec succès');
    onEnabled?.();
  };

  /**
   * Commencer le processus de désactivation
   * Envoie un code EMAIL si la méthode est EMAIL
   */
  const handleStartDisable = async () => {
    clearError();
    setShowDisableConfirm(false);

    try {
      // Si la méthode est EMAIL, envoyer un code
      if (currentMethod === 'EMAIL') {
        await sendEmailCode();
        setCodeSentForDisable(true);
        showAlert('info', 'Un code de vérification a été envoyé à votre email');
      }

      // Afficher le formulaire de désactivation
      setShowDisableForm(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur d\'envoi du code';
      showAlert('error', message);
    }
  };

  /**
   * Confirmer la désactivation avec mot de passe et code
   */
  const handleConfirmDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Validation
    if (!disablePassword) {
      showAlert('error', 'Le mot de passe est requis');
      return;
    }

    if (!disableCode || !/^\d{6,10}$/.test(disableCode)) {
      showAlert('error', 'Le code doit contenir entre 6 et 10 chiffres');
      return;
    }

    try {
      await deactivate2FA(disablePassword, disableCode);

      // Rafraîchir le profil
      await getProfile();

      // Réinitialiser les états
      setShowDisableForm(false);
      setDisablePassword('');
      setDisableCode('');
      setCodeSentForDisable(false);

      showAlert('success', 'Authentification à deux facteurs désactivée');
      onDisabled?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de désactivation';
      showAlert('error', message);
    }
  };

  /**
   * Annuler le formulaire de désactivation
   */
  const handleCancelDisableForm = () => {
    setShowDisableForm(false);
    setDisablePassword('');
    setDisableCode('');
    setCodeSentForDisable(false);
    clearError();
  };

  /**
   * Générer de nouveaux codes de backup
   */
  const handleGenerateBackupCodes = async () => {
    clearError();

    try {
      await generateBackupCodes();
      setViewMode('backup');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de génération';
      showAlert('error', message);
    }
  };

  /**
   * Annuler la configuration
   */
  const handleCancelSetup = () => {
    clearData();
    setViewMode('status');
  };

  /**
   * Obtenir le label de la méthode
   */
  const getMethodLabel = (method?: TwoFactorMethod): string => {
    if (!method) return 'Aucune';
    return method === 'APPLICATION' ? 'Application (TOTP)' : 'Email';
  };

  /**
   * Obtenir l'icône de la méthode
   */
  const getMethodIcon = (method?: TwoFactorMethod) => {
    if (method === 'APPLICATION') {
      return (
        <svg
          className="w-6 h-6 text-accent"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    }

    if (method === 'EMAIL') {
      return (
        <svg
          className="w-6 h-6 text-success"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-6 h-6 text-content-subtle"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
        />
      </svg>
    );
  };

  return (
    <div className={`w-full max-w-4xl ${className}`}>
      {/* Titre */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content">
          Authentification à deux facteurs (2FA)
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Renforcez la sécurité de votre compte avec une couche de protection supplémentaire
        </p>
      </div>

      {/* Alerte */}
      {alert && (
        <div className="mb-6">
          <SecurityAlert
            message={alert.message}
            severity={alert.severity}
            onClose={hideAlert}
            closable
          />
        </div>
      )}

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Vue Statut */}
      {viewMode === 'status' && (
        <div className="space-y-6">
          {/* Statut actuel */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-surface-overlay rounded-lg flex items-center justify-center mr-4">
                  {getMethodIcon(currentMethod)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-content">
                    Statut 2FA
                  </h3>
                  <div className="mt-2 flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        is2FAEnabled
                          ? 'bg-success'
                          : 'bg-surface-overlay'
                      }`}
                    />
                    <span className="text-sm font-medium text-content">
                      {is2FAEnabled ? 'Activée' : 'Désactivée'}
                    </span>
                  </div>
                  {is2FAEnabled && (
                    <p className="mt-1 text-sm text-content-muted">
                      Méthode : {getMethodLabel(currentMethod)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Information */}
          <div className="bg-accent-light border border-accent rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-accent mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-accent">
                  Qu'est-ce que l'authentification à deux facteurs ?
                </h4>
                <p className="mt-1 text-sm text-accent">
                  L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire en
                  exigeant un code de vérification en plus de votre mot de passe lors de la connexion.
                  Nous recommandons fortement d'activer cette fonctionnalité.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {!is2FAEnabled ? (
              <button
                onClick={() => setViewMode('method-selection')}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-6 py-3 bg-accent hover:bg-accent text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                Activer l'authentification à deux facteurs
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={handleGenerateBackupCodes}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center px-6 py-3 bg-accent hover:bg-accent text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Générer de nouveaux codes de backup
                    </>
                  )}
                </button>

                {!showDisableConfirm ? (
                  <button
                    onClick={() => setShowDisableConfirm(true)}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center px-6 py-3 border border-danger text-danger hover:bg-danger-light font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Désactiver l'authentification à deux facteurs
                  </button>
                ) : (
                  <div className="bg-danger-light border border-danger rounded-lg p-4">
                    <p className="text-sm text-danger mb-3">
                      Êtes-vous sûr de vouloir désactiver la 2FA ? Cela réduira la sécurité de votre
                      compte.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDisableConfirm(false)}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2 border border-border text-content-muted bg-surface hover:bg-surface-raised font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleStartDisable}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2 bg-danger hover:bg-danger text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isLoading ? 'Préparation...' : 'Continuer'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bouton retour au dashboard */}
            {onBack && (
              <button
                onClick={onBack}
                className="w-full flex items-center justify-center px-6 py-3 border border-border text-content-muted hover:bg-surface-raised font-medium rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Retour aux paramètres de sécurité
              </button>
            )}
          </div>

          {/* Formulaire de désactivation */}
          {showDisableForm && (
            <div className="mt-6 bg-surface rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-content mb-4">
                Désactivation de l'authentification à deux facteurs
              </h3>

              {codeSentForDisable && (
                <div className="mb-4 p-3 bg-accent-light border border-accent rounded-lg">
                  <p className="text-sm text-accent">
                    Un code de vérification a été envoyé à votre email. Veuillez le saisir ci-dessous.
                  </p>
                </div>
              )}

              <form onSubmit={handleConfirmDisable} className="space-y-4">
                {/* Champ mot de passe */}
                <div>
                  <label
                    htmlFor="disable-password"
                    className="block text-sm font-medium text-content-muted mb-2"
                  >
                    Mot de passe
                  </label>
                  <input
                    id="disable-password"
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Entrez votre mot de passe"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-danger bg-surface text-content"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                {/* Champ code 2FA */}
                <div>
                  <label
                    htmlFor="disable-code"
                    className="block text-sm font-medium text-content-muted mb-2"
                  >
                    {currentMethod === 'EMAIL'
                      ? 'Code de vérification (reçu par email)'
                      : 'Code TOTP (depuis votre application authenticator)'}
                  </label>
                  <input
                    id="disable-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6,10}"
                    maxLength={10}
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                    placeholder={currentMethod === 'EMAIL' ? '000000' : '000000'}
                    className="w-full px-4 py-3 text-center text-2xl tracking-widest font-mono border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-danger bg-surface text-content"
                    disabled={isLoading}
                  />
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelDisableForm}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 border border-border text-content-muted hover:bg-surface-raised font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !disablePassword || !disableCode}
                    className="flex-1 px-4 py-3 bg-danger hover:bg-danger text-content-inverted font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Désactivation...' : 'Désactiver définitivement'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Vue Sélection de méthode */}
      {viewMode === 'method-selection' && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-content mb-2">
              Choisissez votre méthode d'authentification
            </h3>
            <p className="text-sm text-content-muted">
              Sélectionnez comment vous souhaitez recevoir vos codes de vérification
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Méthode EMAIL */}
            <button
              onClick={() => {
                setSelectedMethod('EMAIL');
                setViewMode('email-setup');
              }}
              className="p-6 border-2 border-border rounded-lg hover:border-primary transition-colors text-left group"
            >
              <div className="flex items-start">
                <div className="w-12 h-12 bg-success-light rounded-lg flex items-center justify-center mr-4 transition-colors">
                  <svg
                    className="w-6 h-6 text-success"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-content mb-1">
                    Par Email
                  </h4>
                  <p className="text-sm text-content-muted">
                    Recevez un code à 6 chiffres par email à chaque connexion
                  </p>
                </div>
              </div>
            </button>

            {/* Méthode APPLICATION */}
            <button
              onClick={() => {
                setSelectedMethod('APPLICATION');
                setViewMode('setup');
              }}
              className="p-6 border-2 border-border rounded-lg hover:border-primary transition-colors text-left group"
            >
              <div className="flex items-start">
                <div className="w-12 h-12 bg-accent-light rounded-lg flex items-center justify-center mr-4 transition-colors">
                  <svg
                    className="w-6 h-6 text-accent"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-content mb-1">
                    Application d'authentification
                  </h4>
                  <p className="text-sm text-content-muted">
                    Utilisez Google Authenticator, Microsoft Authenticator ou similaire
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Bouton Annuler */}
          <div className="text-center">
            <button
              onClick={() => {
                setSelectedMethod(null);
                setViewMode('status');
              }}
              className="text-sm text-content-muted hover:text-content"
            >
              ← Annuler
            </button>
          </div>
        </div>
      )}

      {/* Vue Configuration EMAIL */}
      {viewMode === 'email-setup' && <EmailSetupForm userId={userId} onSuccess={handleSetupSuccess} onError={(error) => showAlert('error', error)} onCancel={handleCancelSetup} />}

      {/* Vue Configuration TOTP */}
      {viewMode === 'setup' && (
        <div>
          <TOTPSetupForm
            userId={userId}
            appName={appName}
            onSuccess={handleSetupSuccess}
            onError={(error) => showAlert('error', error)}
            onCancel={handleCancelSetup}
          />
        </div>
      )}

      {/* Vue Codes de backup */}
      {viewMode === 'backup' && backupCodes.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-success-light rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-content">
              Nouveaux codes de backup générés
            </h3>
            <p className="mt-2 text-sm text-content-muted">
              Sauvegardez ces codes en lieu sûr. Ils remplacent les anciens codes.
            </p>
          </div>

          <BackupCodesDisplay codes={backupCodes} />

          <button
            onClick={() => {
              clearData();
              setViewMode('status');
            }}
            className="w-full px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-accent hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Terminer
          </button>
        </div>
      )}
    </div>
  );
}
